import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import {
  ArrowRight,
  FlaskConical,
  ShieldCheck,
  ClipboardCheck,
  Microscope,
  Factory,
  Users,
  Dna,
  type LucideIcon,
} from "lucide-react";
import iconBygherreraadgivning from "@/assets/icon-bygherreraadgivning.svg";
import iconByggeledelse from "@/assets/icon-byggeledelse.svg";
import iconHse from "@/assets/icon-hse.svg";
import iconProjektassistance from "@/assets/icon-projektassistance.svg";
import iconProjektledelse from "@/assets/icon-projektledelse.svg";
import iconProjektoekonomi from "@/assets/icon-projektoekonomi.svg";
import iconShutdown from "@/assets/icon-shutdown.svg";
import iconTidsplanlaegning from "@/assets/icon-tidsplanlaegning.svg";
import { type Locale } from "@/i18n/config";
import { pathFor, type PageId } from "@/i18n/slugMap";
import { BASE_URL, pageHead } from "@/i18n/head";
import { serviceSchema } from "@/lib/schema";
import { pharmaStrings } from "@/i18n/content/pharma";
import { Img, SIZES } from "@/components/Img";

const expertiseIcons: LucideIcon[] = [FlaskConical, Microscope, Dna, ClipboardCheck, ShieldCheck, Factory];
const whyIcons: LucideIcon[] = [Users, ShieldCheck, ClipboardCheck];

// Aligned by index with pharmaStrings.services.
const serviceMeta: { id: PageId; iconSrc: string }[] = [
  { id: "bygherreraadgivning", iconSrc: iconBygherreraadgivning },
  { id: "projektledelse", iconSrc: iconProjektledelse },
  { id: "byggeledelse", iconSrc: iconByggeledelse },
  { id: "tidsplanlaegning", iconSrc: iconTidsplanlaegning },
  { id: "projektoekonomi", iconSrc: iconProjektoekonomi },
  { id: "projektassistance", iconSrc: iconProjektassistance },
  { id: "shutdown", iconSrc: iconShutdown },
  { id: "hse", iconSrc: iconHse },
];

/** <head> for the pharma page: pageHead (canonical/hreflang/breadcrumb) +
 *  Service schema + keywords and Twitter tags. */
export function pharmaHead(locale: Locale) {
  const t = pharmaStrings[locale];
  const base = pageHead("pharma", locale, t.meta, {
    breadcrumbName: t.hero.title,
    schema: [
      serviceSchema({
        name: t.jsonLd.name,
        serviceType: t.jsonLd.serviceType,
        description: t.jsonLd.description,
        url: BASE_URL + pathFor("pharma", locale),
        areaServed: t.jsonLd.areaServed,
        audienceType: t.jsonLd.audienceType,
        image: t.meta.ogImage,
      }),
    ],
  });
  return {
    meta: [
      ...base.meta,
      { name: "keywords", content: t.keywords },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: t.ogTitle },
      { name: "twitter:description", content: t.ogDescription },
      { name: "twitter:image", content: t.meta.ogImage ?? "" },
    ],
    links: base.links,
    scripts: base.scripts,
  };
}

export function PharmaPage({ locale }: { locale: Locale }) {
  const t = pharmaStrings[locale];

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <Img
            src="/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg"
            alt={t.hero.title}
            className="h-full w-full object-cover"
            sizes={SIZES.full}
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-24 md:py-36">
          <p className="text-sm uppercase tracking-widest text-primary font-semibold">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl">{t.hero.title}</h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/85 leading-relaxed max-w-2xl">{t.hero.body}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={pathFor("kontakt", locale)} className="btn-cta">
              {t.hero.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={pathFor("brancher", locale)}
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-8 py-3.5 text-base font-semibold text-foreground hover:bg-white/5"
            >
              {t.hero.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 md:grid-cols-2 items-center">
          <div>
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.intro.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.intro.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.intro.p1}</p>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.intro.p2}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.intro.p3}</p>
          </div>
          <Img
            src="/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg"
            alt={t.intro.heading}
            className="rounded-2xl w-full aspect-[4/3] object-cover"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Expertise grid */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest opacity-70">{t.expertiseSection.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.expertiseSection.heading}</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {t.expertise.map((e, i) => {
              const Icon = expertiseIcons[i] ?? FlaskConical;
              return (
                <div key={e.title} className="rounded-2xl bg-background/5 border border-foreground/10 p-8 backdrop-blur hover:border-primary/40 transition-colors">
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-primary/15 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold">{e.title}</h3>
                  <p className="mt-3 leading-relaxed opacity-90">{e.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services we deliver */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.servicesSection.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.servicesSection.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.servicesSection.intro}</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {t.services.map((s, i) => {
              const m = serviceMeta[i];
              return (
                <Link
                  key={s.title}
                  to={pathFor(m.id, locale)}
                  className="group rounded-2xl bg-secondary/60 border border-white/5 p-8 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/5 transition-all"
                >
                  <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-background/20">
                    <img src={m.iconSrc} alt="" className="h-5 w-5 object-contain brightness-0 invert" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold">{s.title}</h3>
                  <p className="mt-3 text-foreground/85 leading-relaxed text-sm">{s.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all">
                    {t.servicesSection.readMore} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project types */}
      <section className="py-20 bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 md:grid-cols-2 items-center">
          <Img
            src="/wp-content/uploads/MISupportIT_NTK3989_1350x900.jpg"
            alt={t.projectTypesSection.heading}
            className="rounded-2xl w-full aspect-[4/3] object-cover order-last md:order-first"
            sizes={SIZES.split}
          />
          <div>
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.projectTypesSection.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.projectTypesSection.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.projectTypesSection.body}</p>
          </div>
        </div>
      </section>

      {/* Why MI Support */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.why.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.why.heading}</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {t.why.cards.map((v, i) => {
              const Icon = whyIcons[i] ?? Users;
              return (
                <div key={v.title} className="rounded-2xl bg-secondary/60 border border-white/5 p-8">
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-primary/15 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold">{v.title}</h3>
                  <p className="mt-3 text-foreground/85 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.cta.body}</p>
          <Link to={pathFor("kontakt", locale)} className="mt-10 btn-cta">
            {t.cta.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
